import axios, { AxiosInstance } from 'axios';
import { AuthRole, getAccessToken } from '../storage/authTokens';
import { refreshRoleSession, refreshRoleSessionIfNeeded } from './tokenRefresh';

interface RoleAuthInterceptorOptions {
  role: AuthRole;
  endpointNeedsAuth?: (requestUrl: string) => boolean;
  clearSession: () => void;
}

export function attachRoleAuthInterceptors(
  api: AxiosInstance,
  options: RoleAuthInterceptorOptions,
): void {
  api.interceptors.request.use(async (config) => {
    const requestUrl = config.url || '';
    const needsAuth = options.endpointNeedsAuth ? options.endpointNeedsAuth(requestUrl) : true;

    if (needsAuth) {
      const token = await refreshRoleSessionIfNeeded(options.role);
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config as any;
      const requestUrl = config?.url || '';
      const needsAuth = options.endpointNeedsAuth ? options.endpointNeedsAuth(requestUrl) : true;

      if (!axios.isAxiosError(error) || error.response?.status !== 401 || !needsAuth || config?._shiftwagonRetried) {
        return Promise.reject(error);
      }

      config._shiftwagonRetried = true;

      try {
        const nextToken = await refreshRoleSession(options.role);
        if (!nextToken) {
          options.clearSession();
          return Promise.reject(error);
        }

        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${nextToken}`;
        return api(config);
      } catch (refreshError) {
        options.clearSession();
        return Promise.reject(error);
      }
    },
  );
}

export function attachCurrentAccessToken(config: any, role: AuthRole): any {
  const token = getAccessToken(role);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}
